import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  CircularProgress,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { spacesService } from '../services/spaces.service';
import { teamsService } from '../services/teams.service';
import embedsService from '../services/embeds.service';
import diagramsService from '../services/diagrams.service';
import type { Embed, EmbedType } from '../../../shared/src/types/embed.types';
import type { Team } from '@risk-management/shared';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const SpaceSettings: React.FC = () => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [tabValue, setTabValue] = useState(0);
  const [teamAccessDialogOpen, setTeamAccessDialogOpen] = useState(false);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedPermission, setSelectedPermission] = useState('viewer');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Embed form state
  const [embedForm, setEmbedForm] = useState({
    type: 'diagram' as EmbedType,
    targetId: '',
    name: '',
    description: '',
    expiresAt: '',
    noExpiry: true, // Default to no expiry
    includeRisks: false,
  });

  // Fetch space details
  const { data: space, isLoading: spaceLoading } = useQuery({
    queryKey: ['spaces', spaceId],
    queryFn: () => spacesService.getById(spaceId || ''),
    enabled: !!spaceId,
  });

  // Fetch space access permissions
  const { data: accessData, isLoading: accessLoading } = useQuery({
    queryKey: ['space-access', spaceId],
    queryFn: () => spacesService.getAccess(spaceId || ''),
    enabled: !!spaceId,
  });

  // Fetch all teams for dropdown
  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: teamsService.getAll,
  });

  // Fetch embeds for this space
  const { data: embeds, isLoading: embedsLoading } = useQuery({
    queryKey: ['embeds', spaceId],
    queryFn: () => embedsService.getBySpace(spaceId || ''),
    enabled: !!spaceId,
  });

  // Fetch diagrams for embed target selection
  const { data: diagrams } = useQuery({
    queryKey: ['diagrams', spaceId],
    queryFn: () => diagramsService.getAll(spaceId || ''),
    enabled: !!spaceId,
  });

  // Grant team access mutation
  const grantTeamAccessMutation = useMutation({
    mutationFn: () =>
      spacesService.grantTeamAccess(
        spaceId || '',
        selectedTeamId,
        selectedPermission
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space-access', spaceId] });
      setTeamAccessDialogOpen(false);
      setSelectedTeamId('');
      setSelectedPermission('viewer');
    },
  });

  // Revoke team access mutation
  const revokeTeamAccessMutation = useMutation({
    mutationFn: (teamId: string) =>
      spacesService.revokeTeamAccess(spaceId || '', teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space-access', spaceId] });
    },
  });

  // Update team access mutation
  const updateTeamAccessMutation = useMutation({
    mutationFn: ({
      teamId,
      permissionLevel,
    }: {
      teamId: string;
      permissionLevel: string;
    }) =>
      spacesService.updateTeamAccess(spaceId || '', teamId, permissionLevel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space-access', spaceId] });
    },
  });

  // Create embed mutation
  const createEmbedMutation = useMutation({
    mutationFn: () =>
      embedsService.create(spaceId || '', {
        type: embedForm.type,
        targetId: embedForm.targetId,
        name: embedForm.name,
        description: embedForm.description || undefined,
        expiresAt:
          !embedForm.noExpiry && embedForm.expiresAt
            ? new Date(embedForm.expiresAt)
            : undefined,
        includeRisks: embedForm.includeRisks,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embeds', spaceId] });
      setEmbedDialogOpen(false);
      resetEmbedForm();
    },
  });

  // Delete embed mutation
  const deleteEmbedMutation = useMutation({
    mutationFn: (embedId: string) =>
      embedsService.delete(spaceId || '', embedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embeds', spaceId] });
    },
  });

  // Toggle embed active status
  const toggleEmbedMutation = useMutation({
    mutationFn: ({
      embedId,
      isActive,
    }: {
      embedId: string;
      isActive: boolean;
    }) => embedsService.update(spaceId || '', embedId, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embeds', spaceId] });
    },
  });

  const resetEmbedForm = () => {
    setEmbedForm({
      type: 'diagram' as EmbedType,
      targetId: '',
      name: '',
      description: '',
      expiresAt: '',
      noExpiry: true,
      includeRisks: false,
    });
  };

  const handleCopyEmbedUrl = (token: string) => {
    const url = embedsService.getEmbedUrl(token);
    void navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleCopyIframeCode = (token: string) => {
    const url = embedsService.getEmbedUrl(token);
    const iframeId = `crmd-embed-${token.substring(0, 8)}`;
    // Note: ngrok-skip-browser-warning is handled via custom User-Agent or fetch
    const iframeCode = `<iframe id="${iframeId}" src="${url}" width="100%" height="400" frameborder="0" style="border: none; overflow: hidden;" referrerpolicy="no-referrer-when-downgrade"></iframe>
<script>
(function() {
  var iframe = document.getElementById('${iframeId}');
  // Reload iframe with ngrok header via fetch for ngrok tunnels
  if (iframe.src.includes('ngrok')) {
    fetch(iframe.src, { headers: { 'ngrok-skip-browser-warning': 'true' } })
      .then(r => r.text())
      .then(html => { iframe.srcdoc = html; });
  }
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'embed-resize' && e.data.token === '${token}') {
      iframe.style.height = e.data.height + 'px';
    }
  });
})();
</script>`;
    void navigator.clipboard.writeText(iframeCode);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  // Get teams that don't already have access
  const availableTeams = teams?.filter(
    (team: Team) =>
      !accessData?.teams?.some(access => access.teamId === team.id)
  );

  const getPermissionColor = (level: string) => {
    switch (level) {
      case 'admin':
        return 'error';
      case 'editor':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (spaceLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!space) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h5">{t('space.notFound')}</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/spaces')}
          sx={{ mt: 2 }}
        >
          {t('common.back')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/spaces')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          {t('nav.spaces')}
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(`/spaces/${spaceId}`)}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          {space.name}
        </Link>
        <Typography color="text.primary">{t('space.settings')}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(`/spaces/${spaceId}`)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {t('space.settings')}
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('space.access.title')} />
          <Tab label={t('space.embeds.title')} />
        </Tabs>

        {/* Access Management Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">{t('space.access.teams')}</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setTeamAccessDialogOpen(true)}
                disabled={!availableTeams?.length}
              >
                {t('space.access.addTeam')}
              </Button>
            </Box>

            {accessLoading ? (
              <CircularProgress />
            ) : accessData?.teams && accessData.teams.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('team.name')}</TableCell>
                      <TableCell>{t('space.access.permission')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accessData.teams.map(access => (
                      <TableRow key={access.id}>
                        <TableCell>{access.team?.name}</TableCell>
                        <TableCell>
                          <Select
                            value={access.permissionLevel}
                            size="small"
                            onChange={e =>
                              updateTeamAccessMutation.mutate({
                                teamId: access.teamId,
                                permissionLevel: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="viewer">
                              {t('space.access.viewer')}
                            </MenuItem>
                            <MenuItem value="editor">
                              {t('space.access.editor')}
                            </MenuItem>
                            <MenuItem value="admin">
                              {t('space.access.admin')}
                            </MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="error"
                            onClick={() =>
                              revokeTeamAccessMutation.mutate(access.teamId)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">{t('space.access.noTeams')}</Alert>
            )}

            {/* User Access Section */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('space.access.users')}
              </Typography>
              {accessData?.users && accessData.users.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('user.name')}</TableCell>
                        <TableCell>{t('user.email')}</TableCell>
                        <TableCell>{t('space.access.permission')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accessData.users.map(access => (
                        <TableRow key={access.id}>
                          <TableCell>{access.user?.name}</TableCell>
                          <TableCell>{access.user?.email}</TableCell>
                          <TableCell>
                            <Chip
                              label={t(
                                `space.access.${access.permissionLevel}`
                              )}
                              color={getPermissionColor(access.permissionLevel)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">{t('space.access.noUsers')}</Alert>
              )}
            </Box>
          </Box>
        </TabPanel>

        {/* Embeds Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">{t('space.embeds.title')}</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setEmbedDialogOpen(true)}
              >
                {t('space.embeds.create')}
              </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              {t('space.embeds.description')}
            </Alert>

            {embedsLoading ? (
              <CircularProgress />
            ) : embeds && embeds.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('space.embeds.name')}</TableCell>
                      <TableCell>{t('space.embeds.type')}</TableCell>
                      <TableCell>{t('space.embeds.active')}</TableCell>
                      <TableCell>{t('space.embeds.expires')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {embeds.map((embed: Embed) => (
                      <TableRow key={embed.id}>
                        <TableCell>{embed.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(`space.embeds.types.${embed.type}`)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={embed.isActive}
                            onChange={e =>
                              toggleEmbedMutation.mutate({
                                embedId: embed.id,
                                isActive: e.target.checked,
                              })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {embed.expiresAt
                            ? new Date(embed.expiresAt).toLocaleDateString()
                            : t('space.embeds.noExpiry')}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip
                            title={
                              copiedToken === embed.token
                                ? t('common.copied')
                                : t('space.embeds.copyUrl')
                            }
                          >
                            <IconButton
                              onClick={() => handleCopyEmbedUrl(embed.token)}
                            >
                              <LinkIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('space.embeds.copyIframe')}>
                            <IconButton
                              onClick={() => handleCopyIframeCode(embed.token)}
                            >
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            color="error"
                            onClick={() => deleteEmbedMutation.mutate(embed.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">{t('space.embeds.noEmbeds')}</Alert>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Add Team Access Dialog */}
      <Dialog
        open={teamAccessDialogOpen}
        onClose={() => setTeamAccessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('space.access.addTeam')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('team.select')}</InputLabel>
            <Select
              value={selectedTeamId}
              onChange={e => setSelectedTeamId(e.target.value)}
              label={t('team.select')}
            >
              {availableTeams?.map((team: Team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('space.access.permission')}</InputLabel>
            <Select
              value={selectedPermission}
              onChange={e => setSelectedPermission(e.target.value)}
              label={t('space.access.permission')}
            >
              <MenuItem value="viewer">{t('space.access.viewer')}</MenuItem>
              <MenuItem value="editor">{t('space.access.editor')}</MenuItem>
              <MenuItem value="admin">{t('space.access.admin')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamAccessDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => grantTeamAccessMutation.mutate()}
            disabled={!selectedTeamId || grantTeamAccessMutation.isPending}
          >
            {t('space.access.grant')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Embed Dialog */}
      <Dialog
        open={embedDialogOpen}
        onClose={() => {
          setEmbedDialogOpen(false);
          resetEmbedForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('space.embeds.create')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('space.embeds.name')}
            fullWidth
            value={embedForm.name}
            onChange={e => setEmbedForm({ ...embedForm, name: e.target.value })}
            required
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('space.embeds.type')}</InputLabel>
            <Select
              value={embedForm.type}
              onChange={e =>
                setEmbedForm({
                  ...embedForm,
                  type: e.target.value as EmbedType,
                  targetId: '',
                })
              }
              label={t('space.embeds.type')}
            >
              <MenuItem value="diagram">
                {t('space.embeds.types.diagram')}
              </MenuItem>
              <MenuItem value="component">
                {t('space.embeds.types.component')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('space.embeds.target')}</InputLabel>
            <Select
              value={embedForm.targetId}
              onChange={e =>
                setEmbedForm({ ...embedForm, targetId: e.target.value })
              }
              label={t('space.embeds.target')}
            >
              {embedForm.type === 'diagram' &&
                diagrams?.map(diagram => (
                  <MenuItem key={diagram.id} value={diagram.id}>
                    {diagram.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label={t('space.embeds.descriptionLabel')}
            placeholder={t('space.embeds.descriptionPlaceholder')}
            fullWidth
            multiline
            rows={2}
            value={embedForm.description}
            onChange={e =>
              setEmbedForm({ ...embedForm, description: e.target.value })
            }
            sx={{ mt: 2 }}
          />

          {/* Expiry date with checkbox */}
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={embedForm.noExpiry}
                  onChange={e =>
                    setEmbedForm({
                      ...embedForm,
                      noExpiry: e.target.checked,
                      expiresAt: e.target.checked ? '' : embedForm.expiresAt,
                    })
                  }
                />
              }
              label={t('space.embeds.noExpiry')}
            />
            {!embedForm.noExpiry && (
              <TextField
                margin="dense"
                label={t('space.embeds.expiresAt')}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={embedForm.expiresAt}
                onChange={e =>
                  setEmbedForm({ ...embedForm, expiresAt: e.target.value })
                }
              />
            )}
          </Box>

          {/* Include risks checkbox - only for diagrams */}
          {embedForm.type === 'diagram' && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={embedForm.includeRisks}
                    onChange={e =>
                      setEmbedForm({
                        ...embedForm,
                        includeRisks: e.target.checked,
                      })
                    }
                  />
                }
                label={t('space.embeds.includeRisks')}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ ml: 4 }}
              >
                {t('space.embeds.includeRisksHelp')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEmbedDialogOpen(false);
              resetEmbedForm();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => createEmbedMutation.mutate()}
            disabled={
              !embedForm.name ||
              !embedForm.targetId ||
              createEmbedMutation.isPending
            }
          >
            {t('space.embeds.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
